export const handleImagePaste = async (
  event,
  textareaRef,
  content,
  setContent,
  projectId,
  send_request,
  FILE_SERVICE_QUERY_URL
) => {
  const items = (event.clipboardData || event.originalEvent?.clipboardData)?.items;
  if (!items) return;

  for (let item of items) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      event.preventDefault();

      const blob = item.getAsFile();
      // Ensure unique filename to prevent overwriting files with the same default name
      const extension = blob.type.split('/')[1] || 'png';
      const uniqueFile = new File([blob], `note-img-${Date.now()}.${extension}`, {
        type: blob.type,
      });

      const uniquePlaceholderId = `![Uploading image...](loading-${Date.now()})`;

      // Insert loading placeholder at current cursor position
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = content.substring(0, start) + uniquePlaceholderId + content.substring(end);
      setContent(newContent);

      // Move the cursor after the placeholder
      setTimeout(() => {
        // Re-check the ref as it could have become null by the time this callback runs
        if (textareaRef.current) {
          textareaRef.current.setSelectionRange(
            start + uniquePlaceholderId.length,
            start + uniquePlaceholderId.length
          );
        }
      }, 0);

      const formData = new FormData();
      formData.append('file', uniqueFile);
      formData.append('configId', 'fileupload');
      formData.append('type', 'upload');
      formData.append('projectId', projectId);
      formData.append('projectSessionId', '');
      formData.append('formSessionId', '');

      try {
        const json = await send_request(
          'project-service/project/file-library/create',
          formData,
          '',
          'POST',
          { 'content-type': 'multipart/form-data' }
        );

        if (json && json.data) {
          const imageUrl = `${FILE_SERVICE_QUERY_URL}/${json.data.id}`;
          const markdownImage = `![Image](${imageUrl})`;
          // Replace placeholder with the actual markdown image once upload is complete
          setContent(prevContent => prevContent.replace(uniquePlaceholderId, markdownImage));
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        // Remove placeholder on error
        setContent(prevContent => prevContent.replace(uniquePlaceholderId, ''));
      }
      break; // Handle only the first image
    }
  }

  // If the clipboard contains plain text with data URIs we let it paste as-is.
  // These will be converted to uploaded URLs during the save operation.
};

// New helper: convert base64 images in an existing markdown string to file-service URLs
// This will scan the markdown for `![alt](data:image/png;base64,...)` patterns, upload the
// image to the file service and replace the data URI with the returned URL.

/**
 * Replaces base64 encoded images inside a markdown string with uploaded file URLs.
 *
 * @param {string} markdown                       The markdown string that may contain base64 images.
 * @param {string|number} projectId               The current project id – used when uploading.
 * @param {(url: string, data: any, headers: any, method: string, extraHeaders?: object) => Promise<any>} send_request
 *                                               Helper used elsewhere in the codebase to perform the HTTP request.
 * @param {string} FILE_SERVICE_QUERY_URL         Public url of the file-service for querying files.
 * @returns {Promise<string>}                     The updated markdown with remote URLs instead of data URIs.
 */
export const convertBase64ImagesInMarkdown = async (
  markdown,
  projectId,
  send_request,
  FILE_SERVICE_QUERY_URL
) => {
  if (!markdown || typeof markdown !== 'string') return markdown;

  // Matches markdown images pointing to data URIs e.g. ![alt](data:image/png;base64,XXXXX)
  const regex = /!\[([^\]]*)\]\((data:image\/[a-zA-Z0-9+]+;base64,[^)]+)\)/g;
  const matches = [...markdown.matchAll(regex)];

  if (matches.length === 0) return markdown; // Nothing to do

  let updatedMarkdown = markdown;

  for (const match of matches) {
    const fullMatch = match[0]; // Entire ![alt](...)
    const altText = match[1];
    const dataUri = match[2];

    try {
      // Extract mime-type and base64 data
      const headerMatch = dataUri.match(/^data:(image\/[a-zA-Z0-9+]+);base64,(.*)$/);
      if (!headerMatch) continue; // Invalid – skip

      const mimeType = headerMatch[1];
      const base64Data = headerMatch[2];

      // Convert base64 to binary blob
      const byteString = atob(base64Data);
      const byteArray = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) {
        byteArray[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: mimeType });

      // Derive extension from mime
      const extension = mimeType.split('/')[1] || 'png';
      const fileName = `note-img-${Date.now()}.${extension}`;
      const file = new File([blob], fileName, { type: mimeType });

      // Prepare form-data identical to paste handler
      const formData = new FormData();
      formData.append('file', file);
      formData.append('configId', 'fileupload');
      formData.append('type', 'upload');
      formData.append('projectId', projectId);
      formData.append('projectSessionId', '');
      formData.append('formSessionId', '');

      const json = await send_request(
        'project-service/project/file-library/create',
        formData,
        '',
        'POST',
        { 'content-type': 'multipart/form-data' }
      );

      if (json && json.data) {
        const imageUrl = `${FILE_SERVICE_QUERY_URL}/${json.data.id}`;
        const markdownImage = `![${altText}](${imageUrl})`;
        updatedMarkdown = updatedMarkdown.replace(fullMatch, markdownImage);
      }
    } catch (err) {
      console.error('Failed to convert base64 image to file service URL', err);
    }
  }

  return updatedMarkdown;
};
